/* Root element */
export const ROOT_ID = 'root';
export const MOUNT_NODE = document.getElementById(ROOT_ID);

/* Redux saga modes */
export const RESTART_ON_REMOUNT = '@@saga-injector/restart-on-remount';
export const DAEMON = '@@saga-injector/daemon';
export const ONCE_TILL_UNMOUNT = '@@saga-injector/once-till-unmount';

/* API Consumer version */
export const API_CONSUMER_VERSION = '5.1.0';

/* Environment variables */
export const ENVIRONMENT = process.env.NODE_ENV;
export const CSP_ENABLED = process.env.REACT_APP_CSP === 'enabled';
export const API_URL = process.env.REACT_APP_API_URL;
export const STRIPE_PUBLIC_API_KEY =
  process.env.REACT_APP_STRIPE_PUBLIC_API_KEY;
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

/* Auth0 Configuration */
export const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN;
export const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID;
export const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;

/* Intervals */
export const INSTANCES_STATUSES_POLL_INTERVAL = 15000;

/* Burn in */
export const BURN_IN_BEHAVIOURS = {
  FIXED: 'fixed',
  RANDOM: 'random',
  SCROLL: 'scroll'
};
export const BURN_IN_OPACITY_DEFAULT = 0.4;

export const HORIZONTAL_PLACEMENTS = {
  LEFT: 'left',
  MIDDLE: 'middle',
  RIGHT: 'right'
};
export const VERTICAL_PLACEMENTS = {
  TOP: 'top',
  MIDDLE: 'middle',
  BOTTOM: 'bottom'
};
export const BURN_IN_FONT_SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large'
};

/* Credits */
export const CREDIT_STATES = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  INACTIVE: 'inactive',
  SCHEDULED: 'scheduled'
};

/* Attendees */
export const ATTENDEE_STATUSES = {
  INVITED: 'invited',
  LEFT: 'left',
  REMOVED: 'removed',
  VIEWING: 'viewing'
};

/* Modals */
export const MODAL_TYPES = {
  CREDIT_ACTIVATION: 'creditActivation',
  CREDITS_PURCHASE: 'creditsPurchase',
  PAYMENT_METHOD: 'paymentMethod',
  USER: 'user',
  ADD_USERS_TO_GROUP: 'addUsersToGroup'
};

/* Generic error strings */
export const GENERIC_REQUEST_ERROR = 'There was a problem with the request.';

/* Axios */
export const HTTP_VERBS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE'
};

export const FORM_STATUSES = {
  UNRECOVERABLE: 'unrecoverable',
  OKAY: 'okay'
};

export const REQUIRED = '(Required)';

/* Used to control modal form overflow behaviour: smaller forms should generally
 * not scroll, and can cause issues when a `<Select />` is used
 * https://developer.mozilla.org/en-US/docs/Web/CSS/overflow
 */
export const CSS_OVERFLOW = {
  VISIBLE: 'visible',
  HIDDEN: 'hidden',
  SCROLL: 'scroll',
  AUTO: 'auto'
};

export const REQUEST_TIMEOUT = 10000;
export const CREDENTIALS = 'same-origin';

/* Dates */
export const DAY_SECONDS = 24 * 60 * 60;
export const FUTURE = 'future';
export const NOW = 'now';
export const SIMPLE_DATE_FORMAT = 'D MMM YYYY';
export const SIMPLE_TIME_FORMAT = 'h:mm A z';
export const VERBOSE_DATE_FORMAT = 'DD MMM ’YY hh:mm A z';

/* Card brands */
export const CARD_BRANDS = {
  AMEX: 'amex',
  DINERS: 'diners',
  DISCOVER: 'discover',
  JCB: 'jcb',
  MASTERCARD: 'mastercard',
  VISA: 'visa',
  UNION: 'union'
};

/* Stripe constants */
export const STRIPE_PAYMENT_INTENT_STATUSES = {
  REQUIRES_ACTION: 'requires_action',
  SUCCEEDED: 'succeeded'
};

export const STRIPE_REQUEST_TIMEOUT = 30000; // We use a longer timeout for Stripe calls, as they sometimes take a while

// eslint-disable-next-line max-len
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#Locale_identification_and_negotiation#locales_argument
// We infer the locale from the currency we are trying to display: this is not perfect.
export const LOCALES = {
  GBP: 'en-GB',
  USD: 'en-US'
};

/* User Event Types */
export const USER_EVENT_TYPES = {
  LEGACY_CREDIT_BUTTON: 'legacy_activate_credit_button_used'
};

/* Data types */
export const DATA_TYPES = {
  ACTIVE_SESSIONS: 'activeSessions',
  CREDITS: 'credits',
  DETAILS: 'details',
  HISTORICAL_SESSIONS: 'historicalSessions',
  INSTANCES_STATUSES: 'instancesStatuses',
  INSTANCES: 'instances',
  PAYMENT_METHODS: 'paymentMethods',
  SKUS: 'skus',
  USERS: 'users',
  REGIONS: 'regions'
};

export const INITIAL_DATA = [
  DATA_TYPES.DETAILS,
  DATA_TYPES.CREDITS,
  DATA_TYPES.INSTANCES,
  DATA_TYPES.REGIONS
];

export const UI = {
  PROJECT_BAR_WIDTH: '80px',
  SIDEBAR_WIDTH: '200px'
};

export const projectColors = [
  '#ED1B2E',
  '#1B97ED',
  '#8CC753',
  '#FFCC66',
  '#CCFF66',
  '#CCCCCB',
  '#66CCFF',
  '#FF66CC',
  '#FFAB40',
  '#CC66FF'
];

export const URLS = {
  LOGIN_CALLBACK: '/login/callback',
  UNAUTHORISED: '/unauthorised',
  ROOT: '/',
  projectPage: (projectId) => `/projects/${projectId}`
};

export const SENTRY_SEVERITY_LEVELS = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  DEBUG: 'debug',
  LOG: 'log',
  FATAL: 'fatal'
};

/* Saga Types */
export const SAGA_TYPES = {
  CREDITS: 'credits',
  INSTANCES: 'instances',
  ORDERS: 'orders',
  PAYMENT_METHODS: 'paymentMethods',
  PROJECT_DATA: 'projectData',
  PROJECTS: 'projects',
  SESSIONS: 'sessions',
  USERS: 'users',
  REGIONS: 'regions'
};
